import React, { ReactElement, useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import {WebUI, getNid, prefix} from "../../wui-core/src/index";
import Checkbox from './Checkbox';
import CheckboxButton from './CheckboxButton';
import { MenuProps } from '../../wui-menu/src/iMenu';
import Dropdown from '../../wui-dropdown/src';
import Menu from '../../wui-menu/src';
import Button from '../../wui-button/src';
import shallowequal from 'shallowequal';
import { CheckboxGroupProps, CheckboxValueType, CheckboxOtherGroups } from './iCheckbox.js';
import { OrNull } from '../../wui-core/src/types/iCore';
import { WithConfigConsumer } from '../../wui-provider/src/context'
import classnames from 'classnames';

const { Item } = Menu;
const checkboxPrefix = `${prefix}-checkbox`;

const CheckboxGroup: React.FC<CheckboxGroupProps> = (props) => {
    const {
        className = '',
        value,
        defaultValue = [],
        onChange = () => {},
        disabled = false,
        options = [],
        name = '',
        readOnly = false,
        Component = 'div',
        children,
        clsPrefix,
        fieldid,
        optionType = 'default',
        size,
        browser,
        browserShow,
        maxCount: propsMaxCount,
        dropDrownProps,
        bordered,
        browserClassName,
        browserStyle,
    } = props;

    const [values, setValues] = useState<CheckboxValueType[]>(() => {
        return value?.length ? value : defaultValue?.length ? defaultValue : [];
    });
    const [maxCount, setMaxCount] = useState(-1);
    const maxCountRef = useRef<number>(-1);

    const parentResizeObserver = useRef<OrNull<ResizeObserver>>(null);
    const dobounceFillSpaceFun = useRef<(() => void | undefined) | undefined | null>(null);
    const checkboxGroup = useRef<HTMLElement | null>(null);
    const measureContainer = useRef<HTMLElement | null>(null);

    // 添加缓存对象，避免重复DOM读取
    const dimensionCache = useRef<{
        parentWidth: number;
        childDimensions: Array<{width: number; marginLeft: number; marginRight: number}>;
        lastUpdateTime: number;
        isValid: boolean;
    }>({
        parentWidth: 0,
        childDimensions: [],
        lastUpdateTime: 0,
        isValid: false
    });

    // 添加状态来避免初始渲染的闪动
    const [isMaxCountInitialized, setIsMaxCountInitialized] = useState(false);
    const getChildrenCheckbox = useCallback((children: ReactElement, value: CheckboxValueType[]): string[] => {
        if (!children) {
            return []
        }
        let values: string[] = []
        React.Children.forEach((children as ReactElement), child => {
            if (child?.props?.clsPrefix === checkboxPrefix) {
                (value || []).forEach(v => {
                    if (v === child?.props.value) {
                        values.push(v as string)
                    }
                })
            } else {
                values = values.concat(getChildrenCheckbox(child?.props?.children, value))
            }
        })
        return values
    }, []);

    // 优化后的节流函数，使用RAF优化性能
    const throttleRAF = useCallback((fn: () => void, delay: number = 16) => {
        let rafId: number | null = null;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let lastCall = 0;

        return () => {
            const now = Date.now();

            // 如果距离上次调用超过延迟时间，立即执行
            if (now - lastCall >= delay) {
                lastCall = now;
                if (rafId) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
                rafId = requestAnimationFrame(fn);
            } else {
                // 否则延迟执行
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                timeoutId = setTimeout(() => {
                    lastCall = Date.now();
                    if (rafId) {
                        cancelAnimationFrame(rafId);
                        rafId = null;
                    }
                    rafId = requestAnimationFrame(fn);
                }, delay - (now - lastCall));
            }
        };
    }, []);

    // 优化后的尺寸计算函数，添加缓存机制
    const updateDimensionCache = useCallback(() => {
        if (!checkboxGroup.current) return false;

        let parent = checkboxGroup.current.parentElement;
        if (parent && parent.nodeName.toUpperCase() === 'FORM') {
            parent = parent.parentNode as HTMLElement;
        }

        const parentWidth = parent?.clientWidth || 0;
        const source = measureContainer.current || checkboxGroup.current;
        const childNodes = source.childNodes as any;
        const now = Date.now();

        // 检查缓存是否有效（500ms内的缓存认为有效，避免频繁重新计算）
        if (dimensionCache.current.isValid &&
            now - dimensionCache.current.lastUpdateTime < 500 &&
            dimensionCache.current.parentWidth === parentWidth &&
            dimensionCache.current.childDimensions.length === childNodes.length) {
            return true;
        }

        if (!parentWidth || childNodes.length === 0) {
            return false;
        }

        // 批量读取DOM属性，减少回流
        const childDimensions: Array<{width: number; marginLeft: number; marginRight: number}> = [];

        // 一次性读取所有样式，避免多次触发回流
        for (let i = 0; i < childNodes.length; i++) {
            const child = childNodes[i];
            if (child && child.offsetWidth) {
                const style = getComputedStyle(child);
                childDimensions.push({
                    width: child.offsetWidth,
                    marginLeft: parseInt(style.marginLeft) || 0,
                    marginRight: parseInt(style.marginRight) || 0
                });
            }
        }

        // 更新缓存
        dimensionCache.current = {
            parentWidth,
            childDimensions,
            lastUpdateTime: now,
            isValid: true
        };

        return true;
    }, []);

    const setInnerMaxCount = useCallback(() => {
        if (!propsMaxCount || !checkboxGroup.current) {
            return;
        }

        // 使用缓存的尺寸信息
        if (!updateDimensionCache()) {
            return;
        }

        const { parentWidth, childDimensions } = dimensionCache.current;
        const dropDownWidth = 32; // 下拉按钮宽度

        let totalWidth = 0;
        let calculatedMaxCount = childDimensions.length; // 默认显示所有
        for (let i = 0; i < childDimensions.length; i++) {
            const dimension = childDimensions[i];
            const childWidth = dimension.width + dimension.marginLeft + dimension.marginRight;

            // 如果加上这个子元素和下拉按钮会超出父容器宽度，那么从这里开始需要下拉
            if (totalWidth + childWidth + dropDownWidth > parentWidth) {
                calculatedMaxCount = i;
                break;
            }
            totalWidth += childWidth;
        }

        // 只有当需要隐藏某些项目时才设置maxCount
        const newMaxCount = calculatedMaxCount < childDimensions.length ? calculatedMaxCount : -1;

        if (maxCountRef.current !== newMaxCount) {
            maxCountRef.current = newMaxCount;
        }
        if (maxCount !== newMaxCount) {
            setMaxCount(newMaxCount);
        }

        // 标记初始化完成
        if (!isMaxCountInitialized) {
            setIsMaxCountInitialized(true);
        }
    }, [propsMaxCount, updateDimensionCache, maxCount, isMaxCountInitialized]);

    const getMaxCount = useCallback(() => {
        if (propsMaxCount === true && !browser) {
            // 使用优化后的RAF节流函数
            dobounceFillSpaceFun.current = throttleRAF(() => setInnerMaxCount(), 100);

            let parent = checkboxGroup.current!.parentNode;
            if (parent && parent.nodeName.toUpperCase() === 'FORM') {
                parent = parent.parentNode;
            }
            if (parent) {
                parentResizeObserver.current = new ResizeObserver((entries) => {
                    // 优化：只在尺寸真正变化时才重新计算
                    const entry = entries[0];
                    if (entry && entry.contentRect) {
                        const newWidth = entry.contentRect.width;
                        const cachedWidth = dimensionCache.current.parentWidth;

                        // 只有当宽度真正变化且超过阈值时才重新计算，避免微小变化导致的频繁更新
                        if (Math.abs(newWidth - cachedWidth) > 5) {
                            // 使用RAF确保在下一帧执行，避免在布局阶段操作DOM
                            requestAnimationFrame(() => {
                                // 只清除缓存，不立即设置maxCount，避免状态跳跃
                                dimensionCache.current.isValid = false;
                                // 使用节流函数进行延迟计算
                                dobounceFillSpaceFun.current && dobounceFillSpaceFun.current();
                            });
                        }
                    }
                });
                parentResizeObserver.current.observe(parent as Element);
            }
        }
    }, [propsMaxCount, browser]);

    useEffect(() => {
        getMaxCount();
        return () => {
            // 完善资源清理逻辑，防止内存泄漏
            if (parentResizeObserver.current) {
                parentResizeObserver.current.disconnect();
                parentResizeObserver.current = null;
            }
            // 清理节流函数引用
            if (dobounceFillSpaceFun.current) {
                dobounceFillSpaceFun.current = null;
            }
            // 清理缓存
            dimensionCache.current.isValid = false;
            lastChildrenDataRef.current = null;
        };
    }, [getMaxCount]);

    // 处理props变化
    useEffect(() => {
        if (value && !shallowequal(value, values)) {
            setValues(value);
        }
    }, [value, values]);

    useEffect(() => {
        if (children) {
            // 当children改变时，过滤掉不存在的值
            const childValues: string[] = [];
            const getChildValues = (childrenToCheck: ReactElement): void => {
                React.Children.forEach(childrenToCheck, child => {
                    if (child?.props?.clsPrefix === checkboxPrefix) {
                        childValues.push(child.props.value);
                    } else if (child?.props?.children) {
                        getChildValues(child.props.children);
                    }
                });
            };
            getChildValues(children as ReactElement);

            const currentValues = valuesRef.current;
            const filteredValues = currentValues.filter(val => childValues.includes(val as string));
            if (!shallowequal(filteredValues, currentValues)) {
                setValues(filteredValues);
            }
        }
    }, [children]); // 移除values依赖

    // 处理browser更新时的maxCount重新计算，合并到主要的maxCount计算逻辑中
    useEffect(() => {
        if (browser === false && propsMaxCount === true) {
            const timer = setTimeout(() => {
                getMaxCount();
            }, 0);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [browser, propsMaxCount, getMaxCount]);

    // 初始化maxCount计算，避免初始闪动
    useLayoutEffect(() => {
        if (propsMaxCount && !browser && optionType === 'button' && checkboxGroup.current) {
            // 延迟初始化，确保DOM已经渲染
            const timer = setTimeout(() => {
                setInnerMaxCount();
            }, 50);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [propsMaxCount, browser, optionType]);

    // 使用useMemo缓存选项处理结果，避免每次渲染都重新计算
    const processedOptions = useMemo(() => {
        if (!options || !Array.isArray(options)) {
            return [];
        }
        return options.map(option => {
            if (typeof option === 'string') {
                return {
                    label: option,
                    value: option,
                }
            }
            return option;
        });
    }, [options]);

    const setInnerValue = useCallback((newValues: CheckboxValueType[]) => {
        if (!('value' in props)) {
            setValues(newValues);
        }

        if (onChange) {
            onChange(
                newValues
                    .filter(val => newValues.indexOf(val) !== -1)
                    .sort((a, b) => {
                        const indexA = processedOptions.findIndex(opt => opt.value === a);
                        const indexB = processedOptions.findIndex(opt => opt.value === b);
                        return indexA - indexB;
                    }),
            );
        }
    }, [onChange, processedOptions, props]);

    // 使用useRef存储最新的values，确保changeHandle引用稳定
    const valuesRef = useRef(values);
    valuesRef.current = values;

    const changeHandle = useCallback((v: CheckboxValueType) => {
        // 使用ref来获取最新的values，避免闭包问题
        const currentValues = valuesRef.current;
        let newValues = [...currentValues];
        if (newValues.indexOf(v) != -1) {
            newValues.splice(newValues.indexOf(v), 1)
        } else {
            newValues.push(v)
        }
        setInnerValue(newValues);
    }, [setInnerValue]); // 移除values依赖，使用ref获取最新值

    // 优化Menu事件处理函数，减少不必要的重新创建
    const deSelectDropItem: MenuProps['onSelect'] = useCallback(({selectedKeys}: {selectedKeys: string[]}) => {
        setInnerValue(selectedKeys);
    }, [setInnerValue]);

    const selectDropItem: MenuProps['onSelect'] = useCallback(({selectedKeys}: {selectedKeys: string[]}) => {
        setInnerValue(selectedKeys);
    }, [setInnerValue]);

    // 缓存上次的children和计算结果，用于浅比较优化
    // 移除values从缓存key中，因为values变化时只需更新checked状态
    const lastChildrenDataRef = useRef<{
        children: React.ReactElement[];
        maxCount: number;
        disabled: boolean;
        readOnly: boolean;
        browserShow?: boolean;
        browser?: boolean;
        result: React.ReactElement[];
        childrenKeys: string; // 用于快速判断children结构是否变化
    } | null>(null);

    const modifyChildren = useCallback((data: React.ReactElement[]) => {
        // 使用ref获取最新的values
        const currentValues = valuesRef.current;

        // 生成children的结构key，用于判断结构是否变化
        // 注意:必须使用稳定的标识符,不能使用Math.random(),否则每次都会生成不同的key导致缓存失效
        const childrenKeys = data.map((child, index) =>
            child?.key || child?.props?.value || `child-${index}`
        ).join(',');

        // 检查是否可以使用缓存的结构（不包括values）
        const currentCache = lastChildrenDataRef.current;
        const effectiveMaxCount = maxCountRef.current;
        if (currentCache &&
            currentCache.childrenKeys === childrenKeys &&
            currentCache.children === data && // 严格检查children引用是否相同,确保options等没有变化
            currentCache.maxCount === effectiveMaxCount &&
            currentCache.disabled === disabled &&
            currentCache.readOnly === readOnly &&
            currentCache.browserShow === browserShow &&
            currentCache.browser === browser) {
            // 结构相同，只需要更新checked状态，进行浅更新而非重建
            const updatedResult = currentCache.result.map(child => {
                // 只更新checkbox的checked属性，避免重新创建
                if (child?.props?.clsPrefix === checkboxPrefix) {
                    const isChecked = currentValues.includes(child.props.value);
                    // 只有checked状态真正变化时才克隆
                    if (child.props.checked !== isChecked) {
                        return React.cloneElement(child, { checked: isChecked });
                    }
                }
                return child;
            });
            return updatedResult;
        }
        let checkboxData = propsMaxCount && optionType === 'button' && effectiveMaxCount > -1 ? data.slice(0, effectiveMaxCount) : data;
        let dropdownData = propsMaxCount && optionType === 'button' && effectiveMaxCount > -1 ? data.slice(effectiveMaxCount) : [];
        let dropDrownContent = null;

        if (data && !Array.isArray(data)) {
            data = [data]
        }

        // 优化：只对真正需要修改的元素进行克隆
        for (let i = 0; i < checkboxData.length; i++) {
            let childrenProp = checkboxData[i].props?.children
            // 使用与旧代码相同的判断逻辑：通过clsPrefix来识别Checkbox
            if (checkboxData[i].props?.clsPrefix === checkboxPrefix) {
                const currentValue = checkboxData[i].props.value;
                const isChecked = (currentValues as CheckboxValueType[]).includes(currentValue);
                const isDisabled = checkboxData[i].props.disabled || disabled;
                const isReadOnly = readOnly || (browser && browserShow);

                // 只有当状态真正变化时才克隆元素
                if (checkboxData[i].props.checked !== isChecked ||
                    checkboxData[i].props.disabled !== isDisabled ||
                    checkboxData[i].props.readOnly !== isReadOnly ||
                    !checkboxData[i].props.onChange) {
                    checkboxData[i] = React.cloneElement(checkboxData[i],
                        {
                            onChange: () => {
                                if (currentValue !== undefined) {
                                    changeHandle(currentValue);
                                }
                            },
                            checked: isChecked,
                            disabled: isDisabled,
                            readOnly: isReadOnly
                        }
                    )
                }
            } else if (childrenProp) {
                // 递归处理子元素，无论是数组还是单个元素
                const modifiedChildren = Array.isArray(childrenProp)
                    ? modifyChildren(childrenProp)
                    : modifyChildren([childrenProp]);

                checkboxData[i] = React.cloneElement(checkboxData[i], {
                    children: Array.isArray(childrenProp) ? modifiedChildren : modifiedChildren[0]
                });
            }
        }

        if (dropdownData.length) {
            let menuItems: React.ReactElement[] = [];
            React.Children.map(dropdownData, (child, _index) => {
                if (!child) {
                    return null
                }
                const { value, children, disabled: childDisabled, readOnly: childReadOnly } = child?.props;
                const menuKey = value as string;
                const menuContent = children as string;
                let _disabled = childDisabled || childReadOnly || (browser && browserShow);
                const isSelected = (currentValues as CheckboxValueType[]).indexOf(value) !== -1;
                const itemClassName = classnames({
                    [`${checkboxPrefix}-menu-item-read-only`]: childReadOnly,
                    [`${checkboxPrefix}-menu-item-disabled`]: childDisabled,
                    [`${checkboxPrefix}-menu-item-selected`]: isSelected
                });
                menuItems.push(
                    <Item
                        key={menuKey as string}
                        disabled={_disabled}
                        className={itemClassName || undefined}
                    >
                        {menuContent}
                    </Item>
                );
            })
            const _size = !size || size === 'default' ? 'md' : size
            const menu = <Menu selectedKeys={currentValues as string[] || []} onSelect={selectDropItem} onDeselect={deSelectDropItem} multiple>{menuItems}</Menu>
            dropDrownContent =
                <Dropdown {...dropDrownProps} overlay={menu} className={`${clsPrefix}-dropdown ${clsPrefix}-dropdown-${_size}`}>
                    <Button size={_size as any} icon='uf-anglearrowdown'></Button>
                </Dropdown>
        }
        let mergeData = dropDrownContent ? [...checkboxData, dropDrownContent] : checkboxData;

        // 更新缓存，不再缓存values
        lastChildrenDataRef.current = {
            children: data,
            maxCount: effectiveMaxCount,
            disabled,
            readOnly,
            browserShow,
            browser,
            result: mergeData,
            childrenKeys
        };

        return mergeData
    }, [propsMaxCount, optionType, maxCount, disabled, readOnly, browser, browserShow, changeHandle, size, dropDrownProps, clsPrefix, selectDropItem, deSelectDropItem]);

    const renderBrowserOpts = useCallback((optionsList: any) => {
        let selectedTextArr: string[] = [];
        const selectedOptions = optionsList.filter((option: any) => {
            if (typeof option === 'string') {
                return values?.includes(option)
            }
            return option?.value && values?.includes(option?.value)
        })
        if (selectedOptions && selectedOptions.length > 0) {
            selectedOptions.forEach((option: any) => {
                if (typeof option === 'string') {
                    selectedTextArr.push(option)
                } else {
                    selectedTextArr.push(option?.label as string)
                }
            })
        }
        const renderValue = selectedTextArr && selectedTextArr.length > 0 ? selectedTextArr.join('，') : '--';
        const wrapperCls = classnames(`${prefix}-component-browser`, {
            [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom',
            [`${prefix}-component-browser-${size}`]: !!size
        })

        const componentCls = classnames(`${clsPrefix}-browser`, {
            [`${browserClassName}`]: browserClassName
        })
        return (
            <div className={wrapperCls}>
                <div className={componentCls} style={browserStyle} title={renderValue} fieldid={fieldid ? fieldid : undefined}>
                    {renderValue}
                </div>
            </div>
        );
    }, [values, clsPrefix, bordered, browserClassName, browserStyle]);

    const renderBrowserBtns = useCallback((childrenToRender: any) => {
        let selectedTextArr: string[] = [];
        React.Children.forEach(childrenToRender, (child: any) => {
            if (!child || !child.props) return
            if (values && values.includes(child.props.value)) {
                const childContent = child.props.children;
                if (typeof childContent === 'string' || typeof childContent === 'number') {
                    selectedTextArr.push(String(childContent))
                } else if (React.isValidElement(childContent)) {
                    const extractText = (element: React.ReactNode): any => {
                        if (typeof element === 'string' || typeof element === 'number') {
                            return [String(element)];
                        } else if (React.isValidElement(element)) {
                            return extractText(element.props.children);
                        } else if (Array.isArray(element)) {
                            return [element.map(extractText).join('')];
                        }
                        return [''];
                    };
                    selectedTextArr = extractText(childContent);
                }
            }
        })
        const renderValue = selectedTextArr && selectedTextArr.length > 0 ? selectedTextArr.join('，') : '--';
        const wrapperCls = classnames(`${prefix}-component-browser`, {
            [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom',
            [`${prefix}-component-browser-${size}`]: !!size
        })

        const componentCls = classnames(`${clsPrefix}-browser`, {
            [`${browserClassName}`]: browserClassName
        })
        return (
            <div className={wrapperCls}>
                <div className={componentCls} style={browserStyle} title={renderValue}>
                    {renderValue}
                </div>
            </div>
        );
    }, [values, clsPrefix, bordered, browserClassName, browserStyle]);

    // 确保所有Hooks都在早期返回之前调用
    const otherGroupProps: CheckboxOtherGroups = {};
    if (name) {
        otherGroupProps.name = name;
    }
    if (size) {
        otherGroupProps.size = size;
    }

    // 计算组件类名
    let classes = clsPrefix;
    if (className) classes += ' ' + className;
    if (browser && browserShow) classes += ' ' + `${clsPrefix}-browser`;

    // 渲染children内容
    let childrenToRender;
    // 如果有options配置，根据optionType渲染不同类型的组件
    if (options && options.length > 0) {
        if (optionType === 'button') {
            childrenToRender = processedOptions.map((option, index) => (
                <CheckboxButton
                    key={(option.value as CheckboxValueType).toString()}
                    disabled={'disabled' in option ? option.disabled : disabled}
                    readOnly={readOnly}
                    value={option.value}
                    checked={(values as CheckboxValueType[]).indexOf(option.value as CheckboxValueType) !== -1}
                    onChange={() => {
                        if (option.value !== undefined) {
                            changeHandle(option.value);
                        }
                    }}
                    optionType={'button'}
                    fieldid={fieldid ? `${fieldid}-${option.label}-${index}` : undefined}
                    {...otherGroupProps}
                >
                    {option.label}
                </CheckboxButton>
            ));
        } else {
            childrenToRender = processedOptions.map((option, index) => (
                <Checkbox
                    key={(option.value as CheckboxValueType).toString()}
                    disabled={'disabled' in option ? option.disabled : disabled}
                    readOnly={readOnly || (browser && browserShow)}
                    value={option.value}
                    optionType={optionType}
                    checked={(values as CheckboxValueType[]).indexOf(option.value as CheckboxValueType) !== -1}
                    onChange={() => {
                        if (option.value !== undefined) {
                            changeHandle(option.value);
                        }
                    }}
                    fieldid={fieldid ? `${fieldid}-${option.label}-${index}` : undefined}
                    {...otherGroupProps}
                >
                    {option.label}
                </Checkbox>
            ));
        }
    } else {
        // 如果没有options配置，使用传入的children
        childrenToRender = children;
    }

    const originalChildren: React.ReactElement[] = React.Children.toArray(childrenToRender) as React.ReactElement[];
    let adapterNid = getNid(props);

    useEffect(() => {
        if (propsMaxCount && optionType === 'button' && !browser) {
            dimensionCache.current.isValid = false;
            if (dobounceFillSpaceFun.current) {
                dobounceFillSpaceFun.current();
            } else {
                setInnerMaxCount();
            }
        }
    }, [options, children]);

    // 处理browser模式的早期返回，放在所有Hooks调用之后
    if (browser && options && options.length && (optionType === 'button' || !browserShow)) {
        return renderBrowserOpts(options);
    } else if (browser && children && Array.isArray(children) && children.length > 0 && (children[0].type === CheckboxButton || !browserShow)) {
        return renderBrowserBtns(children);
    }

    // 如果启用了maxCount但还未初始化完成，显示占位内容避免闪动
    if (propsMaxCount && !isMaxCountInitialized && optionType === 'button') {
        return (
            <Component
                className={classes}
                {...otherGroupProps}
                {...adapterNid}
                ref={(ref: HTMLElement) => checkboxGroup.current = ref}
            >
                {originalChildren}
            </Component>
        );
    }

    return (
        <>
            <Component
                className={classes}
                {...otherGroupProps}
                {...adapterNid}
                ref={(ref: any) => {
                    checkboxGroup.current = ref as HTMLElement | null;
                }}
            >
                {modifyChildren(originalChildren)}
            </Component>
            <div
                className={`${classes} ${clsPrefix}-measure`}
                style={{ position: 'fixed', left: -99999, top: -99999, visibility: 'hidden', pointerEvents: 'none' }}
                aria-hidden
                role="presentation"
                ref={(ref) => {
                    measureContainer.current = ref as HTMLElement | null;
                }}
            >
                {originalChildren}
            </div>
        </>
    );
};

const CheckboxGroupWithDecorators = WithConfigConsumer()(
    WebUI({name: "checkbox-group"})(CheckboxGroup)
) as React.ComponentClass<Partial<CheckboxGroupProps>>;

export default CheckboxGroupWithDecorators;
